// Shared builder for the per-analysis PTR measures table.
//
// Extracted from RiskScenarioListView.js (SecurityMeasuresTable + ImplementationScheduleTable).
// This is the CANONICAL definition of "the measures of an analysis": the fusion page
// (Fusionnement des PTR) and the backend `collect` endpoint must produce these exact rows,
// otherwise the user sees one list in Atelier 5 and the AI fuses a different one.
//
// Merge order per strategic scenario: customMeasures, dreadedEventMeasures,
// then ecosystemMeasures filtered to the scenario's attack paths. First occurrence
// of a measure id wins (deduped via Map).

// Composite key: measure ids are only unique WITHIN one analysis.
// Fusion code must always namespace with the analysis id.
export const getFusionMeasureKey = (analysisId, measureId) => `${analysisId}::${measureId}`;

const normalizeAttackPathId = (ap) => (typeof ap === 'string' ? ap : ap?.id || ap);

export const buildAnalysisMeasures = (strategicScenarios, riskScenarioIndexMap, options = {}) => {
  const { skipNonRetained = false } = options;
  const seen = new Map();
  for (const ss of (strategicScenarios || [])) {
    const scenarioAttackPathIds = new Set();
    (ss.attackPaths || []).forEach((ap) => {
      const id = normalizeAttackPathId(ap);
      if (id) scenarioAttackPathIds.add(id);
    });

    const resolveAttackPathRef = (m) => {
      const ids = new Set();
      if (m.attackPathId) ids.add(m.attackPathId);
      if (m.attackPaths?.length) m.attackPaths.forEach((ap) => ids.add(normalizeAttackPathId(ap)));
      const refs = [];
      for (const id of ids) {
        if (riskScenarioIndexMap?.[id]) refs.push(riskScenarioIndexMap[id].shortRef);
      }
      return refs.length ? refs.join(', ') : null;
    };

    const pushIfNew = (arr, source) => {
      for (const m of (arr || [])) {
        if (!m || seen.has(m.id)) continue;
        if (skipNonRetained && m.retained === false) continue;
        const enriched = {
          ...m,
          source,
          retained: m.retained !== false,
          strategicScenarioRef: ss.referenceCode || ss.id,
          strategicScenarioName: ss.name,
          strategicScenarioId: ss.id,
        };
        if (source === 'dreadedEvent' && !m.attackPathId && !m.attackPaths?.length && scenarioAttackPathIds.size) {
          enriched.attackPaths = [...scenarioAttackPathIds];
        }
        enriched.displayAttackPathRef = resolveAttackPathRef(enriched);
        seen.set(m.id, enriched);
      }
    };
    pushIfNew(ss.customMeasures, 'custom');
    pushIfNew(ss.dreadedEventMeasures, 'dreadedEvent');
    const ecoMeasures = (ss.ecosystemMeasures || []).filter((m) => {
      const ids = [];
      if (m.attackPaths?.length) m.attackPaths.forEach((ap) => ids.push(normalizeAttackPathId(ap)));
      if (m.attackPathId) ids.push(m.attackPathId);
      return ids.some((id) => scenarioAttackPathIds.has(id));
    });
    pushIfNew(ecoMeasures, 'ecosystem');
  }
  return [...seen.values()];
};

// Fusion input = only measures kept in the PTR.
export const getRetainedMeasures = (measures) => (measures || []).filter((m) => m.retained !== false);
